import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import DatePicker from "react-datepicker"
import Select from 'react-select'
import { Dropdown } from 'react-bootstrap'

import BalanceDetails from './BalanceDetails'
import FilterSearch from 'pages/parts/FilterSearch'
import FilterPagination from 'pages/parts/FilterPagination'

import { httpOk, GET, FILE_D } from 'helpers/api'
import { formatMoney, returnSign, findKeyFromArrayByValue, formatDate, parseLocalDateInput, PAGE_SIZES } from 'helpers/helpers'
import { renderSortIndicator, patchSortFilterOnColumnClick, renderSortDirectionIcon } from 'helpers/tableSortIndicator'

const balanceSortFields = {
	name: 'name',
	product_name: 'product_name',
	price: 'price',
	average_price: 'average_price',
	start_average_price: 'start_average_price',
	start_balance: 'start_balance',
	total_amount_start: 'total_amount_start',
	income_quantity: 'income_quantity',
	income_total_amount: 'income_total_amount',
	expense_quantity: 'expense_quantity',
	expense_total_amount: 'expense_total_amount',
	inventory_plus_quantity: 'inventory_plus_quantity',
	inventory_plus_amount: 'inventory_plus_amount',
	inventory_minus_quantity: 'inventory_minus_quantity',
	inventory_minus_amount: 'inventory_minus_amount',
	end_balance: 'end_balance',
	balance: 'balance',
	total_amount_end: 'total_amount_end',
	total_amount: 'total_amount',
}

function Index() {
	const { t } = useTranslation()

	const reduxAccount = useSelector(state => state.account)

	const quantityOrders = [
		{ 'id': null, 'name': t('all') },
		{ 'id': 'above_zero', 'name': `${t('quantity')} > 0` },
		{ 'id': 'less_zero', 'name': `${t('quantity')} < 0` }
	]

	const [poses, setPoses] = useState([])
	const [wms, setWms] = useState([])
	const [categories, setCategories] = useState([])
	const [productTypes, setProductTypes] = useState([])
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [filterDataExist, setFilterDataExist] = useState(false)
	const [filterData, setFilterData] = useState({
		'date': formatDate(new Date(), 'yyyy-MM-dd'),
		'time': '23:59',
		'pos_id': reduxAccount.pos_id,
		'wms_id': reduxAccount.wms_id,
		'quantity_order': null,
		'category_ids': [],
		'product_type_ids': [],
		'price_mode': 'cost',
		'modalProducts': false,
		'size': 20,
		'search': '',
		'order_by': null,
		'sort': 'asc',
	})
	const [pagination, setPagination] = useState({
		url: '/report-balance',
		size: 20,
		response: null
	})
	const [data, setData] = useState({
		'data': [],
		'total': 0,
		'total_amount': 0,
	})

	async function searchWithFilter(passedData = {}) {
		var filterUrl = "/report-balance"
		var urlParams = ""
		const baseFilter = passedData.filterData != null ? passedData.filterData : filterData
		checkFilter(baseFilter)
		var filterDataCopy = { ...baseFilter }

		if (filterDataCopy.pos_id)
			urlParams += returnSign(urlParams) + 'pos_id=' + filterDataCopy.pos_id
		if (filterDataCopy.wms_id)
			urlParams += returnSign(urlParams) + 'wms_id=' + filterDataCopy.wms_id
		if (filterDataCopy.element_type)
			urlParams += returnSign(urlParams) + 'element_type=' + filterDataCopy.element_type
		if (filterDataCopy.category_ids?.length > 0) {
			const ids = filterDataCopy.category_ids.map(item => item.id).join(',')
			urlParams += returnSign(urlParams) + 'category_ids=' + ids
		}
		if (filterDataCopy.product_type_ids?.length > 0) {
			const ids = filterDataCopy.product_type_ids.map(item => item.id).join(',')
			urlParams += returnSign(urlParams) + 'product_type_ids=' + ids
		}
		if (filterDataCopy.quantity_order)
			urlParams += returnSign(urlParams) + 'quantity_order=' + filterDataCopy.quantity_order
		if (filterDataCopy.price_mode === 'sale')
			urlParams += returnSign(urlParams) + 'price_mode=sale'
		if (filterDataCopy.date)
			urlParams += returnSign(urlParams) + 'date=' + filterDataCopy.date
		if (filterDataCopy.time)
			urlParams += returnSign(urlParams) + 'time=' + encodeURIComponent(filterDataCopy.time)
		if (filterDataCopy.size)
			urlParams += returnSign(urlParams) + 'size=' + filterDataCopy.size
		if (filterDataCopy.search)
			urlParams += returnSign(urlParams) + 'search=' + filterDataCopy.search
		if (filterDataCopy.order_by) {
			const sort = filterDataCopy.sort === 'desc' ? 'desc' : 'asc'
			urlParams += returnSign(urlParams) + 'order_by=' + filterDataCopy.order_by + ',' + sort
		}
		if (filterDataCopy.page)
			urlParams += returnSign(urlParams) + 'page=' + filterDataCopy.page

		filterDataCopy.urlParams = urlParams

		if (passedData['excel']) {
			urlParams += returnSign(urlParams) + 'excel=true'

			const responseExcel = await FILE_D(`${filterUrl + urlParams}`)
			if (httpOk(responseExcel)) {
				// Создаем объект Blob из полученного файла
				const file = new Blob([responseExcel.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

				// Создаем ссылку для скачивания файла
				const fileUrl = URL.createObjectURL(file);

				// Создаем ссылку для скачивания файла в браузере
				const link = document.createElement('a');
				link.href = fileUrl;
				link.setAttribute('download', `balance.xlsx`);
				document.body.appendChild(link);
				link.click();

				// Удаляем ссылку после скачивания файла
				URL.revokeObjectURL(fileUrl);
			}
			return
		}

		if (`/report-balance${urlParams}` !== filterUrl)
			setFilterDataExist(true)

		filterUrl = filterUrl + urlParams

		const response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data)
			setPagination({ ...pagination, 'url': filterUrl, 'response': response.data, 'size': filterDataCopy.size })
		}
		setFilterData(filterDataCopy)
		setDropdownIsOpen(false)
	}

	function resetFilter() {
		const nextFilterData = {
			date: formatDate(new Date(), 'yyyy-MM-dd'),
			time: '23:59',
			pos_id: null,
			category_ids: [],
			product_type_ids: [],
			price_mode: 'cost',
			search: '',
			order_by: null,
			sort: 'asc',
		}
		setFilterData(nextFilterData)
		setFilterDataExist(false)
		searchWithFilter({ filterData: nextFilterData })
	}

	function checkFilter(currentFilterData = filterData) {
		if (
			currentFilterData.date === null &&
			currentFilterData.pos_id === null &&
			currentFilterData.supplier_id === null &&
			!(currentFilterData.category_ids?.length) &&
			!(currentFilterData.product_type_ids?.length) &&
			currentFilterData.search === ''
		) {
			setFilterDataExist(false)
		}
	}

	function toggleDropdown() {
		setDropdownIsOpen(!dropdownIsOpen)
	}

	const filterMinDate = new Date(new Date().getFullYear() - 1, 0, 1)
	const filterMaxDate = new Date(new Date().getFullYear(), 11, 31)

	function shiftFilterDate(days) {
		const base = parseLocalDateInput(
			filterData.date
				? `${filterData.date} ${filterData.time || '23:59'}`
				: null
		) || new Date()
		const next = new Date(base)
		next.setDate(next.getDate() + days)

		const nextDay = new Date(next.getFullYear(), next.getMonth(), next.getDate())
		const minDay = new Date(filterMinDate.getFullYear(), filterMinDate.getMonth(), filterMinDate.getDate())
		const maxDay = new Date(filterMaxDate.getFullYear(), filterMaxDate.getMonth(), filterMaxDate.getDate())
		if (nextDay < minDay || nextDay > maxDay) return

		setFilterData({
			...filterData,
			date: formatDate(next, 'yyyy-MM-dd'),
			time: filterData.time || '23:59',
		})
	}

	async function paginate(page) {
		const nextFilterData = { ...filterData, 'page': page }
		setFilterData(nextFilterData)
		searchWithFilter({ filterData: nextFilterData })
	}

	function handlePageSizeChange(size) {
		const nextFilterData = { ...filterData, size, page: null }
		setFilterData(nextFilterData)
		searchWithFilter({ filterData: nextFilterData })
	}

	function balanceSortFieldLabel(field) {
		if (field === balanceSortFields.name || field === balanceSortFields.product_name) return t('name2')
		if (
			field === balanceSortFields.price ||
			field === balanceSortFields.average_price ||
			field === balanceSortFields.start_average_price
		) return t('stock_average_cost')
		if (field === balanceSortFields.start_balance) return `${t('remaining_start')} / ${t('quantity')}`
		if (field === balanceSortFields.total_amount_start) return `${t('remaining_start')} / ${t('amount')}`
		if (field === balanceSortFields.income_quantity) return `${t('income2')} / ${t('quantity')}`
		if (field === balanceSortFields.income_total_amount) return `${t('income2')} / ${t('amount')}`
		if (field === balanceSortFields.expense_quantity) return `${t('expense')} / ${t('quantity')}`
		if (field === balanceSortFields.expense_total_amount) return `${t('expense')} / ${t('amount')}`
		if (field === balanceSortFields.inventory_plus_quantity) return `${t('inventory')} (${t('surplus')}) / ${t('quantity')}`
		if (field === balanceSortFields.inventory_plus_amount) return `${t('inventory')} (${t('surplus')}) / ${t('amount')}`
		if (field === balanceSortFields.inventory_minus_quantity) return `${t('inventory')} (${t('shortage')}) / ${t('quantity')}`
		if (field === balanceSortFields.inventory_minus_amount) return `${t('inventory')} (${t('shortage')}) / ${t('amount')}`
		if (field === balanceSortFields.end_balance || field === balanceSortFields.balance) return `${t('remaining_end')} / ${t('quantity')}`
		if (field === balanceSortFields.total_amount_end || field === balanceSortFields.total_amount) return `${t('remaining_end')} / ${t('amount')}`
		return field
	}

	function handleBalanceTableSort(fieldKey) {
		const nextFilterData = patchSortFilterOnColumnClick(filterData, fieldKey)
		setFilterData(nextFilterData)
		searchWithFilter({ filterData: nextFilterData })
	}

	function renderSortableHeader(fieldKey, label, options = {}) {
		const {
			className = 'text-end',
			style,
			rowSpan,
		} = options
		return (
			<th
				rowSpan={rowSpan}
				style={style}
				className={`${className} th-sortable user-select-none`}
				onClick={() => handleBalanceTableSort(fieldKey)}
				title={t('sort_by')}>
				<div className={`d-flex align-items-center gap-2 ${className.includes('text-center') ? 'justify-content-center' : className.includes('text-end') ? 'justify-content-end' : 'justify-content-between'}`}>
					<span>{label}</span>
					{renderSortIndicator(fieldKey, filterData)}
				</div>
			</th>
		)
	}

	async function toggleModal(bool = false, item = {}) {
		setFilterData({
			...filterData,
			'element_id': item.element_id,
			'element_name': item.product_name,
			'modalProducts': bool,
		})
	}

	function calculateBalance(item) {
		var result = Number(item.start_balance) +
			Number(item.income_quantity) -
			Number(item.expense_quantity) +
			Number(item.inventory_plus_quantity) -
			Number(item.inventory_minus_quantity)

		if (
			Math.round(result * 1000) / 1000 !== Math.round(Number(item.end_balance) * 1000) / 1000) {
			return 'fw-700'
		}

		return ''
	}

	async function getPoses() {
		const response = await GET('/helper/poses')
		if (httpOk(response)) setPoses(response.data)
	}

	async function getWms(pos_id = null) {
		var filterPosId = pos_id ?? filterData.pos_id
		const response = await GET(`/helper/wms?pos_id=${filterPosId}`)
		if (httpOk(response)) setWms(response.data)
	}

	async function getCategories() {
		const response = await GET('/helper/categories')
		if (httpOk(response)) setCategories(response.data)
	}

	async function getProductTypes() {
		const response = await GET(`/helper/product-types`)
		if (httpOk(response)) setProductTypes(response.data)
	}

	useEffect(() => {
		getPoses()
		getWms()
		getCategories()
		getProductTypes()
		searchWithFilter()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header">
				<h4 className="vertical-center h-100">
					{t('balance2')} [{data.total}]
				</h4>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex justify-content-between flex-wrap mb-3">
						<div className="d-flex flex-wrap gap-2">
							<div>
								<FilterSearch
									filterData={filterData}
									setFilterData={setFilterData}
								onSearchChange={(search) => {
									const nextFilterData = { ...filterData, search, page: null }
									setFilterData(nextFilterData)
									searchWithFilter({ filterData: nextFilterData })
								}}>
								</FilterSearch>
							</div>
							<div>
								<Dropdown onToggle={toggleDropdown} show={dropdownIsOpen}>
									<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
										{t('filter')}
									</Dropdown.Toggle>

									<Dropdown.Menu className="dropdown-filter">
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('pos')}</label>
												<Select
													options={poses}
													value={poses.find(option => option.id === filterData.pos_id) || ''}
													onChange={(option) => {
														setFilterData({ ...filterData, 'pos_id': option.id })
														getWms(option.id)
													}}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
											<div className="form-group col-md-6">
												<label>{t('wms')}</label>
												<Select
													// isClearable
													options={wms}
													value={wms.find(option => option.id === filterData.wms_id) || ''}
													onChange={(option) => setFilterData({ ...filterData, 'wms_id': option ? option.id : null })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
										</div>
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('category')}</label>
												<Select
													isMulti
													isClearable
													options={categories}
													value={filterData.category_ids || []}
													onChange={(options) => setFilterData({ ...filterData, 'category_ids': options || [] })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
											<div className="form-group col-md-6">
												<label>{t('type')}</label>
												<Select
													isMulti
													isClearable
													options={productTypes}
													value={filterData.product_type_ids || []}
													onChange={(options) => setFilterData({ ...filterData, 'product_type_ids': options || [] })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
										</div>
										<div className="row">
											<div className="form-group col-md-12">
												<label>{t('price')}</label>
												<div className="d-flex align-items-center flex-nowrap gap-3">
													<div className="form-check mb-0">
														<input
															className="form-check-input"
															type="checkbox"
															id="balance-price-mode-cost"
															checked={filterData.price_mode !== 'sale'}
															onChange={() => setFilterData({ ...filterData, price_mode: 'cost' })}
														/>
														<label className="form-check-label" htmlFor="balance-price-mode-cost">
															{t('cost_price')}
														</label>
													</div>
													<div className="form-check mb-0">
														<input
															className="form-check-input"
															type="checkbox"
															id="balance-price-mode-sale"
															checked={filterData.price_mode === 'sale'}
															onChange={() => setFilterData({ ...filterData, price_mode: 'sale' })}
														/>
														<label className="form-check-label" htmlFor="balance-price-mode-sale">
															{t('sale_price')}
														</label>
													</div>
												</div>
											</div>
										</div>
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('date')}</label>
												<div className="d-flex align-items-center gap-1">
													<button
														type="button"
														className="btn btn-outline-secondary btn-sm px-2"
														title="-1"
														onClick={() => shiftFilterDate(-1)}
													>
														<i className="uil uil-angle-left-b"></i>
													</button>
													<div className="calendar-input flex-grow-1">
														<DatePicker
															className="form-control"
															dateFormat="dd.MM.yyyy HH:mm"
															showMonthDropdown
															showYearDropdown
															dropdownMode="select"
															yearDropdownItemNumber={2}
															minDate={filterMinDate}
															maxDate={filterMaxDate}
															selected={parseLocalDateInput(
																filterData.date
																	? `${filterData.date} ${filterData.time || '23:59'}`
																	: null
															)}
															onChange={(date) => setFilterData({
																...filterData,
																date: date ? formatDate(date, 'yyyy-MM-dd') : null,
																time: date ? formatDate(date, 'HH:mm') : '23:59',
															})}
															showTimeSelect
															timeFormat="HH:mm"
															timeIntervals={60}
															timeCaption={t('time')}
														/>
														<i className="uil uil-calendar-alt"></i>
													</div>
													<button
														type="button"
														className="btn btn-outline-secondary btn-sm px-2"
														title="+1"
														onClick={() => shiftFilterDate(1)}
													>
														<i className="uil uil-angle-right-b"></i>
													</button>
												</div>
											</div>
											<div className="form-group col-md-6">
												<label>{t('quantity')}</label>
												<Select
													options={quantityOrders}
													value={quantityOrders.find(option => option.id === filterData.quantity_order) || ''}
													onChange={(option) => setFilterData({ ...filterData, 'quantity_order': option.id })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
										</div>
										<div className="row mt-3">
											<div className="col-md-12">
												<div className="d-flex justify-content-end gap-2">
													<button className="btn btn-secondary"
														onClick={() => setDropdownIsOpen(false)}>
														{t('cancel')}
													</button>
													<button className="btn btn-primary"
														onClick={() => searchWithFilter({ filterData: { ...filterData } })}>
														{t('apply')}
													</button>
												</div>
											</div>
										</div>
									</Dropdown.Menu>
								</Dropdown>
							</div>
							<div>
								<button className="btn btn-outline-primary btn-wide-2"
									onClick={() => searchWithFilter({ 'excel': true })}>
									<i className="uil uil-file-download-alt"></i>
								</button>
							</div>
						</div>
						<div className="d-flex flex-wrap gap-5">
							<div className="vertical-center">
								<h5 className="d-flex justify-content-between gap-2">
									<b>{t('income2')}:</b>
									<b>{formatMoney(data.income_total_amount)}</b></h5>
								<h5 className="d-flex justify-content-between gap-2">
									<b>{t('expense')}:</b>
									<b>{formatMoney(data.expense_total_amount)}</b></h5>
							</div>
							<div className="vertical-center">
								<h5 className="d-flex justify-content-between gap-2">
									<b>{t('remaining_start')}:</b>
									<b>{formatMoney(data.total_amount_start)}</b>
								</h5>
								<h5 className="d-flex justify-content-between gap-2">
									<b>{t('remaining_end')}:</b>
									<b>{formatMoney(data.total_amount_end)}</b>
								</h5>
							</div>
						</div>
					</div>

					{filterDataExist &&
						<div className="filter-block">
							<div className="filter">
								<strong>{t('filter')}</strong>
								<br />
								<span className="me-2">
									<b>{t('date')}: </b>
									{filterData.date &&
										<span>
											{formatDate(filterData.date)}
											{filterData.time ? ` ${filterData.time}` : ''}
										</span>
									}
								</span>
								{filterData.pos_id &&
									<span className="me-2">
										<b>{t('pos')}: </b>
										{findKeyFromArrayByValue(poses, 'id', filterData.pos_id, 'name')}
									</span>
								}
								{filterData.wms_id &&
									<span className="me-2">
										<b>{t('wms')}: </b>
										{findKeyFromArrayByValue(wms, 'id', filterData.wms_id, 'name')}
									</span>
								}
								{filterData.quantity_order &&
									<span className="me-2">
										<b>{t('quantity')}: </b>
										{findKeyFromArrayByValue(quantityOrders, 'id', filterData.quantity_order, 'name')}
									</span>
								}
								{filterData.search &&
									<span className="me-2">
										<b>{t('search')}: </b>
										<span>{filterData.search}</span>
									</span>
								}
								{filterData.order_by &&
									<span className="me-2 d-inline-flex align-items-center flex-wrap gap-1">
										<b>{t('sort_by')}: </b>
										<span className="d-inline-flex align-items-center gap-1">
											{balanceSortFieldLabel(filterData.order_by)}
											{renderSortDirectionIcon(filterData.sort === 'desc' ? 'desc' : 'asc', {
												ariaLabel: filterData.sort === 'desc' ? t('sort_descending') : t('sort_ascending'),
												title: filterData.sort === 'desc' ? t('sort_descending') : t('sort_ascending'),
											})}
										</span>
									</span>
								}
							</div>
							<div className="vertical-center">
								<button className="btn btn-outline-dark text-uppercase"
									onClick={resetFilter}>
									{t('reset')}
								</button>
							</div>
						</div>
					}

					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th rowSpan={2} className="text-center" style={{ 'width': '33px' }}>#</th>
									{renderSortableHeader(balanceSortFields.name, t('name2'), { rowSpan: 2, className: '' })}
									{renderSortableHeader(balanceSortFields.price, t('stock_average_cost'), { rowSpan: 2, className: 'text-center' })}
									<th colSpan={2} className="text-center">{t('remaining_start')}</th>
									<th colSpan={2} className="text-center">{t('income2')}</th>
									<th colSpan={2} className="text-center">{t('expense')}</th>
									<th colSpan={2} className="text-center td-ellipsis" title={t('surplus')}>{t('inventory')} ({t('surplus')})</th>
									<th colSpan={2} className="text-center td-ellipsis" title={t('shortage')}>{t('inventory')} ({t('shortage')})</th>
									<th colSpan={2} className="text-center">{t('remaining_end')}</th>
								</tr>
								<tr>
									{renderSortableHeader(balanceSortFields.start_balance, t('quantity'))}
									{renderSortableHeader(balanceSortFields.total_amount_start, t('amount'))}
									{renderSortableHeader(balanceSortFields.income_quantity, t('quantity'))}
									{renderSortableHeader(balanceSortFields.income_total_amount, t('amount'))}
									{renderSortableHeader(balanceSortFields.expense_quantity, t('quantity'))}
									{renderSortableHeader(balanceSortFields.expense_total_amount, t('amount'))}
									{renderSortableHeader(balanceSortFields.inventory_plus_quantity, t('quantity'))}
									{renderSortableHeader(balanceSortFields.inventory_plus_amount, t('amount'))}
									{renderSortableHeader(balanceSortFields.inventory_minus_quantity, t('quantity'))}
									{renderSortableHeader(balanceSortFields.inventory_minus_amount, t('amount'))}
									{renderSortableHeader(balanceSortFields.end_balance, t('quantity'))}
									{renderSortableHeader(balanceSortFields.total_amount_end, t('amount'))}
								</tr>
							</thead>
							<tbody>
								{data?.data?.map((item, index) => (
									<tr key={index}>
										<td className="text-center">{item.row_number}</td>
										<td className="text-primary cursor table-first-column-clickable"
											onClick={() => toggleModal(true, item)}>
											{item.product_name}
										</td>
										<td className="text-end">{formatMoney(item.start_average_price)}</td>
										<td className="text-end">{formatMoney(item.start_balance, 3)}</td>
										<td className="text-end">{formatMoney(item.total_amount_start)}</td>

										<td className="text-end">{formatMoney(item.income_quantity, 3)}</td>
										<td className="text-end">{formatMoney(item.income_total_amount)}</td>
										<td className="text-end">{formatMoney(item.expense_quantity, 3)}</td>
										<td className="text-end">{formatMoney(item.expense_total_amount)}</td>

										<td className="text-end">{formatMoney(item.inventory_plus_quantity, 3)}</td>
										<td className="text-end">{formatMoney(item.inventory_plus_amount)}</td>

										<td className="text-end">{formatMoney(item.inventory_minus_quantity, 3)}</td>
										<td className="text-end">{formatMoney(item.inventory_minus_amount)}</td>

										<td className="text-end">
											<span className={calculateBalance(item)}>{formatMoney(item.end_balance, 3)}</span>
										</td>
										<td className="text-end">{formatMoney(item.total_amount_end)}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{pagination.response &&
						<FilterPagination
							pagination={pagination}
							paginate={paginate}
							showPageSizeSelect
							pageSizeOptions={PAGE_SIZES}
							onPageSizeChange={handlePageSizeChange}>
						</FilterPagination>
					}
				</div>
			</div>

			{filterData.modalProducts &&
				<BalanceDetails
					filterDataParent={filterData}
					setFilterDataParent={setFilterData}
					poses={poses}
					wms={wms}>
				</BalanceDetails>
			}
		</>
	)
}

export default Index